import { ShowModal } from '@sketch/components'
import {
  CommandBarItem,
  compactCommands,
  createCommand,
} from '@sketch/modules-common'

import {
  ArchiveStorageItemsModal,
  DeleteStorageItemsModal,
  MoveStorageItemModal,
  MoveStorageItemModalAction,
} from '../modals'
import { StorageItemIdentity } from '../types'

import { ReactComponent as MoveIcon } from '@sketch/icons/img-arrows-right-left-17'
import { ReactComponent as ArchiveIcon } from '@sketch/icons/img-box-17'
import { ReactComponent as TrashIcon } from '@sketch/icons/trash-empty-17'

interface CreateSelectedItemsCommandsProps {
  canArchive: boolean
  canDelete: boolean
  canMove: boolean
  canRestore: boolean
  onClearSelection: () => void
  parentProjectIdentifier: string | null
  restoreAction: MoveStorageItemModalAction
  selectedItems: StorageItemIdentity[]
  showModal: ShowModal
  workspaceIdentifier: string
}

export const createSelectedItemsCommands = ({
  canArchive,
  canDelete,
  canMove,
  canRestore,
  onClearSelection,
  parentProjectIdentifier,
  restoreAction,
  selectedItems,
  showModal,
  workspaceIdentifier,
}: CreateSelectedItemsCommandsProps): CommandBarItem[] => {
  if (selectedItems.length === 0) {
    return []
  }

  return compactCommands([
    canMove &&
      createCommand(
        {
          id: 'selected-items.move',
          analyticsId: 'selected-items.move',
          title: 'Move Selected Items…',
          section: 'Selected Items',
          keywords: ['move', 'selected', 'selection'],
          icon: MoveIcon,
          priority: 20,
        },
        () =>
          showModal(MoveStorageItemModal, {
            items: selectedItems,
            workspaceIdentifier,
            parentProjectIdentifier,
            action: 'move',
            originType: 'standard',
            onMove: onClearSelection,
          })
      ),
    canRestore &&
      createCommand(
        {
          id: 'selected-items.restore',
          analyticsId: `selected-items.${restoreAction}`,
          title: 'Restore Selected Items…',
          section: 'Selected Items',
          keywords: ['restore', 'unarchive', 'selected', 'selection'],
          icon: MoveIcon,
          priority: 18,
        },
        () =>
          showModal(MoveStorageItemModal, {
            items: selectedItems,
            workspaceIdentifier,
            parentProjectIdentifier,
            action: restoreAction,
            originType: 'stashed',
            onMove: onClearSelection,
          })
      ),
    canArchive &&
      createCommand(
        {
          id: 'selected-items.archive',
          analyticsId: 'selected-items.archive',
          title: 'Archive Selected Items…',
          section: 'Selected Items',
          keywords: ['archive', 'selected', 'selection'],
          icon: ArchiveIcon,
          priority: 15,
        },
        () =>
          showModal(ArchiveStorageItemsModal, {
            items: selectedItems,
            onArchive: onClearSelection,
          })
      ),
    canDelete &&
      createCommand(
        {
          id: 'selected-items.delete',
          analyticsId: 'selected-items.delete',
          title: 'Delete Selected Items…',
          section: 'Selected Items',
          keywords: ['delete', 'trash', 'remove', 'selected', 'selection'],
          icon: TrashIcon,
          priority: 10,
        },
        () =>
          showModal(DeleteStorageItemsModal, {
            items: selectedItems,
            workspaceId: workspaceIdentifier,
            onDelete: onClearSelection,
          })
      ),
  ])
}
